import React from 'react';
import { useSelector } from 'react-redux';

import Redirect from '@swf/components/Redirect/Redirect';
import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import { Header } from '@swf/core/enums';
import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { CompetitionCard, CompetitionTabs, TryAgainMessage } from '@swf/sports/components';
import { Tab } from '@swf/sports/components/CompetitionTabs/CompetitionTabs';
import GroupedTemplatePanels from '@swf/sports/components/GroupedTemplatePanels/GroupedTemplatePanels';
import LiveDataWarning from '@swf/sports/components/LiveDataWarning/LiveDataWarning';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import useScoreboardPolling from '@swf/sports/hooks/useScoreboardPolling/useScoreboardPolling';
import { getEventTypesWithInPlayEventIdsFromMarketViews } from '@swf/sports/utils/getEventTypesWithInPlayEventIds';
import { notEmpty } from '@swf/sports/utils/notEmpty';

import { TennisGroups } from './components/TennisGroups';
import { sportsCompetitionEventsWidgetModel, useSportsCompetitionEventsWidget } from './model';

const SportsCompetitionEventsWidget: WidgetFC = () => {
  const {
    widgetSelect,
    widgetDispatch,
    widgetConfig: { shouldApplyTennisGrouping }
  } = useSportsCompetitionEventsWidget();

  const errorCode = useSelector(widgetSelect.errorCode);
  const templateGroups = useSelector(widgetSelect.templateGroups);
  const showOutrights = useSelector(widgetSelect.showOutrights);
  const competitionName = useSelector(widgetSelect.competitionName);
  const eventTypeId = useSelector(widgetSelect.eventTypeId);
  const isLoading = useSelector(widgetSelect.isLoading);
  const markets = useSelector(widgetSelect.markets);
  const marketIds = useSelector(widgetSelect.marketIds);
  const marketTemplateData = useSelector(widgetSelect.marketTemplateData);
  const hasInPlayMarkets = useSelector(widgetSelect.hasInPlayMarkets);
  const urlParams = useSelector(select.sports.getUrlParams);

  useScoreboardPolling(
    getEventTypesWithInPlayEventIdsFromMarketViews(Object.values(markets).filter(notEmpty))
  );

  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);

  if (isLoading) {
    return null;
  }

  if (errorCode === 404) {
    return null;
  }

  if (showOutrights && !templateGroups.length) {
    return <Redirect alias={'sportsCompetitionOutrights'} values={urlParams} />;
  }

  return (
    <>
      <Vary by={[Header.TimeZone]} />
      <CompetitionCard competitionName={competitionName} />
      {templateGroups.length ? (
        <CompetitionTabs hiddenTabs={showOutrights ? [] : [Tab.OUTRIGHTS]} />
      ) : null}
      {errorCode ? (
        <TryAgainMessage />
      ) : templateGroups.length && eventTypeId ? (
        <>
          {shouldApplyTennisGrouping ? (
            <TennisGroups />
          ) : (
            <GroupedTemplatePanels
              groupedOn="DATE"
              groups={templateGroups}
              {...marketTemplateData}
            />
          )}
          {hasInPlayMarkets ? <LiveDataWarning /> : null}
        </>
      ) : null}
    </>
  );
};

SportsCompetitionEventsWidget.model = sportsCompetitionEventsWidgetModel;

export default withWidgetTestId(SportsCompetitionEventsWidget, 'sports-competition-events');
